import "./currentuser.scss";
import {
    type FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Link } from "react-router-dom";
import { ResponsiveLine } from "@nivo/line";
import {
    Button,
    ButtonColour,
    ButtonStyle,
    getButtonStyling,
    LayerContentContext,
    noop,
    Select,
    type SelectOptionType,
    Sidebar,
    Toggle,
    UIFloatingPortal,
    useMediaQuery,
} from "@onshopfront/components";
import axios from "axios";
import moment from "moment";
import { Application } from "../../../_Bootstrap/Application";
import { useShopfrontDispatch, useShopfrontSelector } from "../../../_Bootstrap/hooks";
import { showDialog } from "../../../Actions/Dialogs/DialogActions";
import { LAYOUT_BODY_CLICK, LAYOUT_USER_MENU_TOGGLE, stopLoading } from "../../../Actions/General/LayoutActions";
import { changeRegister } from "../../../Actions/General/LocationActions";
import { toggleTraining } from "../../../Actions/Information/InformationActions";
import { userLogout } from "../../../Actions/Users/UserActions";
import { OKOptionButton } from "../../../Common/Buttons/Button";
import { useDarkMode } from "../../../Common/DarkMode/DarkModeHooks";
import PopupManager from "../../../Common/Popups/PopupManager";
import { determineDataSource } from "../../../Data/DataStores/DataSource";
import { usePermissions } from "../../../Utilities/Domain/Users/Permissions";
import { fireEventType } from "../../../Utilities/Generic/ActionsHelper";
import { currency } from "../../../Utilities/Generic/Numbers";
import { CancellablePromise } from "../../../Utilities/Generic/Promises";
import { uppercaseFirst } from "../../../Utilities/Generic/Strings";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { getLogLevel, LogLevel } from "../../../Utilities/Platform/Logging";
import { subscribeToOption, unsubscribeFromOption } from "../../../Utilities/Platform/Options/Options";
import { getFullReport } from "../../../Utilities/Platform/Reports";
import { AudioMenu } from "../AudioMenu/AudioMenu";
import { UserMenuHelpButton } from "../UserMenuHelpButton";

type ReportDataPoints = Array<{ x: string; y: number; }>;

type ReportType = {
    id: string;
    colour: string;
    data: ReportDataPoints;
};

function createTestData(): ReportDataPoints {
    const results: ReportDataPoints = [];

    const end = moment().endOf("day");
    const current = moment().startOf("day").subtract(6, "days");

    while(current.isBefore(end)) {
        const day = current.format("ddd");

        results.push({
            x: day,
            y: Math.round(20 * Math.random()),
        });

        current.add(1, "day");
    }

    return results;
}

const permissions = [ "see_user_menu_graphs", "change_register" ] as const;

type ThemeTypes = "default" | "light" | "dark" | "auto";

const THEME_OPTIONS: Array<SelectOptionType<ThemeTypes>> = [{
    label      : "Inherit",
    value      : "default",
    description: "Use the Shopfront settings to display the page in",
}, {
    label      : "Light",
    value      : "light",
    description: "Force light mode across Shopfront",
}, {
    label      : "Dark",
    value      : "dark",
    description: "Force dark mode across Shopfront",
}, {
    label      : "System",
    value      : "auto",
    description: "Use the operating system settings to display Shopfront as light / dark",
}];

export const UserMenu: FC = () => {
    const prevUserRef = useRef<false | string>(false);

    const open = useShopfrontSelector(state => state.layout.userMenu.open);
    const training = useShopfrontSelector(state => state.information.training);
    const user = useShopfrontSelector(state => state.user.id);
    const outlet = useShopfrontSelector(state => state.location.outlet);
    const register = useShopfrontSelector(state => state.location.register);
    const isGlobal = useShopfrontSelector(state => state.location.isGlobal);

    const smallScreen = useMediaQuery("(max-width: 768px)");

    const darkMode = useDarkMode();

    const dispatch = useShopfrontDispatch();

    const [ loading, setLoading ] = useState(true);
    const [ revenue, setRevenue ] = useState<Array<ReportType>>([]);
    const [ transactions, setTransactions ] = useState<Array<ReportType>>([]);
    const [ showTraining, setShowTraining ] = useState(false);

    const [ userDarkMode, setUserDarkMode ] = useState<SelectOptionType<ThemeTypes>>({
        label: "Default",
        value: "default",
    });

    const { seeUserMenuGraphs, changeRegister: canChangeRegister } = usePermissions(permissions);

    const testData = useMemo(
        () => createTestData(),
        []
    );

    useEffect(() => {
        const updateShowTraining = (option: "register_training_sales_mode_enabled", value: boolean): void => {
            setShowTraining(value);
        };

        const value = subscribeToOption(
            "register_training_sales_mode_enabled",
            updateShowTraining
        );

        setShowTraining(value);

        return () => {
            unsubscribeFromOption("register_training_sales_mode_enabled", updateShowTraining);
        };
    }, []);

    useEffect(() => {
        if(!user) {
            return;
        }

        determineDataSource()
            .then(source => {
                return source.users.get(user);
            })
            .then(dbUser => {
                setUserDarkMode({
                    label: uppercaseFirst(dbUser.dark_mode),
                    value: dbUser.dark_mode,
                });
            })
            .catch(err => {
                if(getLogLevel() === LogLevel.VERBOSE) {
                    console.error("Unable to load local user for user menu", err);
                }
            });
    }, [ user ]);

    useEffect(() => {
        if(!open) {
            return;
        }

        if(!seeUserMenuGraphs) {
            setLoading(false);
            setRevenue([]);
            setTransactions([]);

            return;
        }

        if(prevUserRef.current !== user) {
            setLoading(true);
        }

        prevUserRef.current = user;

        const query = `SELECT revenue, transaction_count FROM sales BY user ON DATE_FORMAT(timestamp, "YYYY-MM-DD") WHERE user_id = "${user}" START "6 days ago"`;
        const cancelToken = axios.CancelToken.source();

        const cancellablePromise = new CancellablePromise<any>(
            getFullReport(
                query,
                {
                    ignoreDivisionByZero: true,
                    disableFormat       : true,
                    sendWidestValues    : false,
                    cancelToken         : cancelToken.token,
                    serverCache         : {
                        id    : `ui-user-${user}-menu`,
                        expiry: moment().add(5, "minutes"),
                    },
                },
                false
            ),
            () => {
                cancelToken.cancel("Promise Cancelled");
            }
        );

        cancellablePromise
            .promise
            .then(({ data }) => {
                const end = moment(data.range.end).endOf("day");
                const current = moment(data.range.start).startOf("day");

                const revenue = [];
                const transactions = [];

                while(current.isBefore(end)) {
                    const key = current.format("YYYY-MM-DD");
                    const day = current.format("ddd");

                    if(data.data[key]) {
                        revenue.push({
                            x: day,
                            y: data.data[key].totals.revenue,
                        });

                        transactions.push({
                            x: day,
                            y: data.data[key].totals.transaction_count,
                        });
                    } else {
                        revenue.push({
                            x: day,
                            y: 0,
                        });

                        transactions.push({
                            x: day,
                            y: 0,
                        });
                    }

                    current.add(1, "day");
                }

                setLoading(false);
                setRevenue([{
                    id    : "Revenue",
                    colour: "rgb(94, 187, 235)",
                    data  : revenue,
                }]);
                setTransactions([{
                    id    : "Transactions",
                    colour: "rgb(94, 187, 235)",
                    data  : transactions,
                }]);
            })
            .catch(err => {
                console.error("Unable to load and format user data for the graphs", err);
            });

        return () => {
            cancellablePromise.cancel();
        };
    }, [ seeUserMenuGraphs, user, open ]);

    const getData = (name: "revenue" | "transactions"): Array<ReportType> => {
        if(loading) {
            return [{
                id    : "Loading",
                colour: "rgb(10, 10, 10)",
                data  : testData,
            }];
        }

        return name === "revenue" ? revenue : transactions;
    };

    const onLogout = (): void => {
        if(getLogLevel() === LogLevel.VERBOSE) {
            console.log("Logging out due to user menu logout pressed");
        }

        dispatch(fireEventType(LAYOUT_BODY_CLICK));
        dispatch(userLogout(true));
    };

    const onToggleTraining = (): void => {
        dispatch(toggleTraining());
    };

    const onClose = useCallback(() => {
        dispatch(fireEventType(LAYOUT_USER_MENU_TOGGLE));
    }, [ dispatch ]);

    return (
        <UIFloatingPortal>
            <Sidebar
                open={open}
                onClose={onClose}
                side="right"
                className="user-menu z-1 bg-white dark:bg-dark-900"
                wrapperClassName="flex flex-col md:w-[500px] w-screen max-w-full h-full"
                top={50}
            >
                {smallScreen && (
                    <ul className="list-none m-0 px-8 pt-4">
                        <UserMenuHelpButton />
                        <li className="text-xl my-1 truncate w-full">
                            <button
                                className={
                                    "group flex items-center border-0 bg-transparent text-xl py-1 px-0 m-0 " +
                                    "w-full ignore-default text-left text-neutral-900 hover:text-neutral-500 " +
                                    "dark:text-neutral-100 dark:hover:text-neutral-400 cursor-pointer min-h-12"
                                }
                                onClick={() => {
                                    if(!canChangeRegister) {
                                        PopupManager.info(
                                            "You don't have permission to change the current location"
                                        );

                                        return;
                                    }

                                    dispatch(stopLoading());
                                    dispatch(changeRegister());
                                }}
                            >
                                <span className="inline-block mr-4 text-primary-dark dark:text-primary">
                                    <FontAwesomeLight icon="map-signs" />
                                </span>
                                <div>
                                    {isGlobal ? (
                                        <div>Global</div>
                                    ) : (
                                        <>
                                            {!register?.name ? (
                                                <div>
                                                    {outlet?.name}
                                                </div>
                                            ) : (
                                                <>
                                                    <div
                                                        className={
                                                            "text-xs text-neutral-800 dark:text-neutral-400 " +
                                                            "group-hover:text-neutral-500 " +
                                                            "dark:group-hover:text-neutral-500"
                                                        }
                                                    >
                                                        {outlet?.name}
                                                    </div>
                                                    <div className="text-lg">
                                                        {register?.name}
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            </button>
                        </li>
                        <li className="text-xl my-1 truncate w-full">
                            <button
                                className={
                                    "border-0 bg-transparent text-xl py-1 px-0 m-0 inline-block w-full text-left " +
                                    "ignore-default text-neutral-900 hover:text-neutral-500 " +
                                    "dark:text-neutral-100 dark:hover:text-neutral-400 cursor-pointer min-h-12"
                                }
                                onClick={() => {
                                    dispatch(showDialog(
                                        "Audio Manager",
                                        <div className="text-left max-h-[400px] overflow-auto">
                                            <AudioMenu />
                                        </div>,
                                        "question",
                                        [ OKOptionButton ]
                                    ));
                                }}
                            >
                                <span className="inline-block mr-4 text-primary-dark dark:text-primary">
                                    <FontAwesomeLight icon="volume-up" />
                                </span>
                                Audio Manager
                            </button>
                        </li>
                    </ul>
                )}
                <div className="px-8 mt-0 first:mt-4 space-y-2">
                    {showTraining && (
                        <label
                            className={
                                "group flex items-center justify-between border-0 bg-transparent text-xl py-1 px-0 " +
                                "m-0 w-full text-left ignore-default text-neutral-900 dark:text-neutral-100 " +
                                "cursor-pointer min-h-12"
                            }
                        >
                            <div className="group-hover:text-neutral-500 dark:group-hover:text-neutral-400">
                                <span className="inline-block mr-4 text-primary-dark dark:text-primary">
                                    <FontAwesomeLight icon="hard-hat" />
                                </span>
                                Training Mode
                            </div>
                            <Toggle
                                checked={training}
                                onChange={onToggleTraining}
                                name="training-mode-toggle"
                            />
                        </label>
                    )}
                    <label
                        className={
                            "group flex items-center justify-between border-0 bg-transparent text-xl py-1 px-0 m-0 " +
                            "w-full text-left ignore-default text-neutral-900 dark:text-neutral-100 cursor-pointer " +
                            "min-h-12"
                        }
                    >
                        <div className="group-hover:text-neutral-500 dark:group-hover:text-neutral-400">
                            <span className="inline-block mr-4 text-primary-dark dark:text-primary">
                                <FontAwesomeLight icon="paint-roller" />
                            </span>
                            Theme
                        </div>
                        <LayerContentContext.Provider
                            value={{
                                zIndex: 10,
                                id    : "",
                                remove: noop,
                            }}
                        >
                            <Select
                                preventInput
                                className="text-base max-w-[150px]"
                                value={userDarkMode}
                                overrideMenuWidth={350}
                                onChange={(option) => {
                                    setUserDarkMode(option);

                                    Application.get("DarkModeManager")
                                        .setUserDarkMode(option.value as ThemeTypes)
                                        .catch(err => {
                                            if(getLogLevel() >= LogLevel.NORMAL) {
                                                console.error("Unable to update theme", err);
                                            }

                                            PopupManager.error("Error trying to save the user's theme");
                                        });
                                }}
                                options={THEME_OPTIONS}
                            />
                        </LayerContentContext.Provider>
                    </label>
                </div>
                {seeUserMenuGraphs && (
                    <>
                        <div className="user-graph grow relative mt-8 min-h-32 max-h-64 w-full overflow-hidden">
                            <div
                                className={
                                    "absolute -top-1 left-0 right-0 text-neutral-700 dark:text-neutral-300 " +
                                    "text-center text-sm"
                                }
                            >
                                Revenue
                            </div>
                            <ResponsiveLine
                                data={getData("revenue")}
                                curve="monotoneX"
                                enableArea
                                areaOpacity={0.1}
                                colors={(el: ReportType): string => el.colour}
                                colorBy={() => ""}
                                enableSlices="x"
                                yFormat={v => currency(v as number)}
                                margin={{
                                    top   : 30,
                                    left  : 75,
                                    bottom: 25,
                                    right : 35,
                                }}
                                axisBottom={{
                                    tickSize    : 5,
                                    tickPadding : 5,
                                    tickRotation: -30,
                                }}
                                axisLeft={{
                                    format: (v: number): string => currency(v),
                                }}
                                theme={{
                                    axis: {
                                        ticks: {
                                            text: {
                                                fill: darkMode ? "#FFFFFF" : undefined,
                                            },
                                        },
                                    },
                                    tooltip: {
                                        container: { // bg-dark-700
                                            background: darkMode ? "#333333" : undefined,
                                        },
                                    },
                                }}
                                pointLabelYOffset={-12}
                            />
                        </div>
                        <div
                            className="user-graph grow relative mb-4 mt-8 min-h-32 max-h-64 w-full overflow-hidden"
                        >
                            <div
                                className={
                                    "absolute -top-1 left-0 right-0 text-neutral-700 dark:text-neutral-300 " +
                                    "text-center text-sm"
                                }
                            >
                                Transaction Count
                            </div>
                            <ResponsiveLine
                                data={getData("transactions")}
                                curve="monotoneX"
                                enableArea
                                areaOpacity={0.1}
                                colors={(el: ReportType): string => el.colour}
                                colorBy={() => ""}
                                enableSlices="x"
                                margin={{
                                    top   : 30,
                                    left  : 75,
                                    bottom: 25,
                                    right : 35,
                                }}
                                axisBottom={{
                                    tickSize    : 5,
                                    tickPadding : 5,
                                    tickRotation: -30,
                                }}
                                axisLeft={{
                                    format: (value: string | number): string => (
                                        Number.isInteger(value) ? value.toString() : ""
                                    ),
                                }}
                                theme={{
                                    axis: {
                                        ticks: {
                                            text: {
                                                fill: darkMode ? "#FFFFFF" : undefined,
                                            },
                                        },
                                    },
                                    tooltip: {
                                        container: { // bg-dark-700
                                            background: darkMode ? "#333333" : undefined,
                                        },
                                    },
                                }}
                                pointLabelYOffset={-12}
                            />
                        </div>
                    </>
                )}
                <div className="flex-1" />
                <div className="grid sm:grid-cols-2 grid-cols-1 gap-x-8 gap-y-4 p-8">
                    <Link
                        to={`/settings/users/${user}/edit`}
                        className={
                            "px-8 py-2 no-underline inline-block button-like text-center " +
                            getButtonStyling(ButtonStyle.Outlined, ButtonColour.Blue, false)
                        }
                    >
                        <FontAwesomeLight icon="cog" /> Modify User
                    </Link>
                    <Button
                        type="button"
                        style={ButtonStyle.Filled}
                        colour={ButtonColour.Primary}
                        onClick={onLogout}
                    >
                        <FontAwesomeLight icon="sign-out" /> Logout
                    </Button>
                </div>
            </Sidebar>
        </UIFloatingPortal>
    );
};
